import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, readApiError } from '../../lib/api';

interface UserRow {
  id: number;
  email: string;
  fullName: string;
  phone: string | null;
  isActive: boolean;
  isSuperAdmin: boolean;
  role: { code: string; name: string };
  lastLoginAt: string | null;
  createdAt: string;
}

interface RoleRow {
  id: number;
  code: string;
  name: string;
  _count: { users: number };
}

export function UsersPage() {
  const qc = useQueryClient();
  const users = useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get<UserRow[]>('/api/users')).data,
  });
  const roles = useQuery({
    queryKey: ['users', 'roles'],
    queryFn: async () => (await api.get<RoleRow[]>('/api/users/roles')).data,
  });

  const [showInvite, setShowInvite] = useState(false);
  const [resetting, setResetting] = useState<UserRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inviteMut = useMutation({
    mutationFn: async (body: { email: string; fullName: string; phone?: string; roleCode: string; password: string }) => {
      await api.post('/api/users', body);
    },
    onSuccess: () => {
      setShowInvite(false);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const updateMut = useMutation({
    mutationFn: async (vars: { id: number; body: Partial<{ fullName: string; phone: string | null; roleCode: string; isActive: boolean }> }) => {
      await api.patch(`/api/users/${vars.id}`, vars.body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (err) => setError(readApiError(err).message),
  });

  const resetMut = useMutation({
    mutationFn: async (vars: { id: number; password: string }) => {
      await api.post(`/api/users/${vars.id}/reset-password`, { password: vars.password });
    },
    onSuccess: () => setResetting(null),
    onError: (err) => setError(readApiError(err).message),
  });

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Usuarios y roles</h1>
        <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
          + Invitar usuario
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem 1rem' }}>Nombre</th>
              <th style={{ padding: '0.75rem 1rem' }}>Email</th>
              <th style={{ padding: '0.75rem 1rem' }}>Rol</th>
              <th style={{ padding: '0.75rem 1rem' }}>Estado</th>
              <th style={{ padding: '0.75rem 1rem' }}>Último ingreso</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.75rem 1rem' }}>
                  {u.fullName}
                  {u.isSuperAdmin && (
                    <span style={{ background: 'var(--color-accent)', color: '#1a1a1a', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', marginLeft: 6 }}>
                      SUPER
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>{u.email}</td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <select
                    className="input"
                    value={u.role.code}
                    disabled={u.isSuperAdmin}
                    onChange={(e) => updateMut.mutate({ id: u.id, body: { roleCode: e.target.value } })}
                    style={{ padding: '4px 8px' }}
                  >
                    {roles.data?.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  {u.isActive ? (
                    <span style={{ color: 'var(--color-accent)' }}>activo</span>
                  ) : (
                    <span style={{ color: 'var(--color-muted)' }}>inactivo</span>
                  )}
                </td>
                <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem', color: 'var(--color-muted)' }}>
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('es-CO') : '—'}
                </td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                  <button
                    className="btn"
                    style={{ padding: '4px 10px', fontSize: '0.85rem', marginRight: 6 }}
                    onClick={() => setResetting(u)}
                  >
                    Cambiar pass
                  </button>
                  {!u.isSuperAdmin && (
                    <button
                      className="btn"
                      style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                      onClick={() => updateMut.mutate({ id: u.id, body: { isActive: !u.isActive } })}
                    >
                      {u.isActive ? 'Desactivar' : 'Activar'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.data?.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin usuarios.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {roles.data && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Roles disponibles</h3>
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            {roles.data.map((r) => (
              <li key={r.id}>
                <code>{r.code}</code> — {r.name}{' '}
                <small style={{ color: 'var(--color-muted)' }}>· {r._count.users} usuario(s)</small>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showInvite && roles.data && (
        <InviteModal
          roles={roles.data}
          onCancel={() => setShowInvite(false)}
          onSubmit={(b) => inviteMut.mutate(b)}
          submitting={inviteMut.isPending}
        />
      )}

      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onCancel={() => setResetting(null)}
          onSubmit={(password) => resetMut.mutate({ id: resetting.id, password })}
          submitting={resetMut.isPending}
        />
      )}
    </section>
  );
}

function InviteModal(props: {
  roles: RoleRow[];
  onCancel: () => void;
  onSubmit: (b: { email: string; fullName: string; phone?: string; roleCode: string; password: string }) => void;
  submitting: boolean;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleCode, setRoleCode] = useState(props.roles[0]?.code ?? 'cashier');
  const [password, setPassword] = useState('');

  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 460 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({ email, fullName, phone: phone || undefined, roleCode, password });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Invitar usuario</h3>
        <div className="field">
          <label>Email</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label>Nombre completo</label>
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Teléfono (opcional)</label>
            <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="field">
            <label>Rol</label>
            <select className="input" value={roleCode} onChange={(e) => setRoleCode(e.target.value)}>
              {props.roles.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label>Contraseña inicial (≥ 8 caracteres)</label>
          <input className="input" type="text" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required />
          <small style={{ color: 'var(--color-muted)' }}>
            Compártesela en privado al usuario; podrá cambiarla después.
          </small>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Invitando...' : 'Invitar'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal(props: {
  user: UserRow;
  onCancel: () => void;
  onSubmit: (password: string) => void;
  submitting: boolean;
}) {
  const [password, setPassword] = useState('');
  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 380 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit(password);
        }}
      >
        <h3 style={{ marginTop: 0 }}>Cambiar contraseña</h3>
        <p style={{ color: 'var(--color-muted)' }}>
          Para <strong>{props.user.fullName}</strong> ({props.user.email}).
        </p>
        <div className="field">
          <label>Nueva contraseña</label>
          <input
            className="input"
            type="text"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Guardando...' : 'Cambiar'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};
